// État d'une campagne côté page publique.
//
// Le serveur est la SEULE source de vérité (backend/services/etat_campagne.py) :
// il envoie un bloc « etat » avec la page campagne. Ici, on ne fait que le
// LIRE et le mettre en forme (date de fin, compte à rebours). Aucune règle
// métier n'est recalculée, sauf un repli minimal quand l'API ne l'envoie pas
// encore (pendant une mise en ligne, l'ancien serveur peut répondre au
// nouveau site) : la page se comporte alors exactement comme avant.

export const OUVERTE = "ouverte";
export const BROUILLON = "brouillon";
export const EN_PAUSE = "en_pause";
export const TERMINEE = "terminee";

// Le compte à rebours n'apparaît que dans les 72 dernières heures : avant,
// la date en toutes lettres suffit (une urgence affichée trop tôt s'use).
export const FENETRE_COMPTE_A_REBOURS_MS = 72 * 3600 * 1000;

const ETAT_REPLI = {
  code: OUVERTE,
  dons_acceptes: true,
  objectif_atteint: false,
  pourcentage: null,
  reste: null,
  reste_unites: null,
  fin_le: null,
  jours_restants: null,
};

/** Bloc « etat » de la campagne ; `fourni` = false si l'API ne l'a pas envoyé. */
export function lireEtat(campaign) {
  const e = campaign?.etat;
  if (!e || typeof e !== "object" || !e.code) return { ...ETAT_REPLI, fourni: false };
  return { ...ETAT_REPLI, ...e, fourni: true };
}

/**
 * Chiffres de la jauge publique : valeur promise, objectif, pourcentage.
 *   - paniers (unit_qty) : en UNITÉS ;
 *   - collecte (thermometer) : en EUROS ;
 *   - places (seats_request) : pas de jauge (objectif null).
 * Le pourcentage vient du serveur (arrondi vers le bas, sans plafond). Repli
 * sans bloc « etat » : même règle, calculée ici.
 */
export function chiffresJauge(campaign, etat) {
  const stats = campaign?.stats || {};
  const type = campaign?.type || "thermometer";
  if (type === "seats_request") return { valeur: 0, objectif: null, pourcentage: null };
  const unites = type === "unit_qty";
  const valeur = Number(unites ? stats.units_committed : stats.committed) || 0;
  const brut = unites
    ? Number(campaign?.target_units) || 0
    : Number(stats.target || campaign?.target_amount) || 0;
  const objectif = brut > 0 ? brut : null;
  let pourcentage = null;
  if (objectif) {
    pourcentage = etat?.fourni && etat.pourcentage != null
      ? etat.pourcentage
      : Math.floor((valeur * 100) / objectif);
  }
  return { valeur, objectif, pourcentage };
}

/**
 * Jours restants avant la fin (0 = dernier jour), null sans date de fin ou
 * si la campagne est terminée — la règle du SERVEUR (minuit heure de Paris),
 * jamais une pastille « Dernier jour » sur une campagne finie. Repli sans
 * bloc « etat » : l'ancien calcul.
 */
export function joursRestants(campaign, etat = lireEtat(campaign)) {
  if (etat.fourni) return etat.code === TERMINEE ? null : (etat.jours_restants ?? null);
  if (!campaign?.end_date) return null;
  return Math.max(0, Math.ceil((new Date(campaign.end_date) - new Date()) / 86400000));
}

/**
 * Chiffres d'une CARTE de campagne (accueil, toutes les campagnes, fiche de
 * la synagogue) : les MÊMES que la page campagne et la vignette WhatsApp —
 * le PROMIS (montant arrondi vers le bas, pourcentage du serveur) — sinon le
 * donateur lisait « 35 % » sur la carte et « 41 % » en cliquant.
 *   - unites    : paniers (valeur et objectif en unités) ;
 *   - largeur   : largeur de la barre (0 à 100) ; le pourcentage, lui, peut
 *                 dépasser 100 % ;
 *   - objectif  : null → pas de jauge (places, ou pas d'objectif).
 */
export function chiffresCarte(campaign) {
  const etat = lireEtat(campaign);
  const { valeur, objectif, pourcentage } = chiffresJauge(campaign, etat);
  const unites = (campaign?.type || "thermometer") === "unit_qty";
  return {
    etat,
    unites,
    valeur: unites ? valeur : Math.floor(valeur),
    objectif,
    pourcentage,
    largeur: Math.max(0, Math.min(100, pourcentage || 0)),
    joursRestants: joursRestants(campaign, etat),
  };
}

// ── Dates ────────────────────────────────────────────────────────────────
// « 2026-10-15 » → { a: 2026, m: 10, j: 15 }, null si illisible.
function lireJour(iso) {
  const r = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ""));
  if (!r) return null;
  return { a: Number(r[1]), m: Number(r[2]), j: Number(r[3]) };
}

/**
 * « jeudi 15 octobre » (l'année n'est ajoutée que si ce n'est pas l'année
 * en cours). Le jour est lu tel quel, sans fuseau : aucun décalage d'un jour
 * possible selon le téléphone.
 */
export function texteJour(iso, maintenant = new Date()) {
  const d = lireJour(iso);
  if (!d) return null;
  const options = { weekday: "long", day: "numeric", month: "long", timeZone: "UTC" };
  if (d.a !== maintenant.getFullYear()) options.year = "numeric";
  return new Intl.DateTimeFormat("fr-FR", options).format(new Date(Date.UTC(d.a, d.m - 1, d.j, 12)));
}

// Décalage (minutes) de l'heure de Paris sur UTC à l'instant `t` (ms).
function decalageParis(t) {
  const morceaux = new Intl.DateTimeFormat("en-US", {
    timeZone: "Europe/Paris", hourCycle: "h23",
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  }).formatToParts(new Date(t));
  const v = {};
  morceaux.forEach((p) => { v[p.type] = p.value; });
  const commeUtc = Date.UTC(+v.year, +v.month - 1, +v.day, +v.hour % 24, +v.minute, +v.second);
  return Math.round((commeUtc - t) / 60000);
}

/**
 * Instant (ms) où la campagne se termine : minuit, heure de Paris, au début
 * du LENDEMAIN du jour de fin — même règle que le serveur (le dernier jour
 * compte en entier, été comme hiver). null si la date est illisible ou si
 * le navigateur ne connaît pas les fuseaux.
 */
export function instantFin(iso) {
  const d = lireJour(iso);
  if (!d) return null;
  const minuitUtc = Date.UTC(d.a, d.m - 1, d.j + 1, 0, 0, 0);
  try {
    let t = minuitUtc - decalageParis(minuitUtc) * 60000;
    // 2e passage : le décalage est relu à l'instant trouvé (changement d'heure).
    t = minuitUtc - decalageParis(t) * 60000;
    return t;
  } catch {
    return null;
  }
}

/**
 * « 2 jours 5 h », « 5 h 07 min », « 12 min » ; null hors des 72 dernières
 * heures ou une fois l'heure passée (c'est alors le serveur qui tranche :
 * la page n'annonce jamais « terminée » d'après l'horloge du téléphone).
 * Minutes arrondies vers le HAUT : jamais « 0 min » tant que c'est ouvert.
 */
export function texteCompteARebours(restantMs) {
  if (restantMs == null || !(restantMs > 0) || restantMs > FENETRE_COMPTE_A_REBOURS_MS) return null;
  const total = Math.ceil(restantMs / 60000);
  const jours = Math.floor(total / 1440);
  const heures = Math.floor((total % 1440) / 60);
  const minutes = total % 60;
  if (jours > 0) return `${jours} jour${jours > 1 ? "s" : ""}${heures > 0 ? ` ${heures} h` : ""}`;
  if (heures > 0) return `${heures} h ${String(minutes).padStart(2, "0")} min`;
  return `${minutes} min`;
}

/**
 * Compte à rebours à afficher maintenant, ou null. Garde-fou : le serveur
 * doit lui aussi situer la campagne dans ses derniers jours
 * (`jours_restants` ≤ 3) — un téléphone mal réglé n'invente pas d'urgence.
 */
export function compteARebours(etat, maintenant = Date.now()) {
  if (!etat?.dons_acceptes || etat.jours_restants == null || etat.jours_restants > 3) return null;
  const fin = instantFin(etat.fin_le);
  if (fin == null) return null;
  return texteCompteARebours(fin - maintenant);
}
